/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals firebase */

import $ from '../Util/Util';

/**
 * Represents the Firebase handler class.
 */

export default class Firebase {

    /** Authentication providers. */
    static get PROVIDER() { return {
        EMAILPASSWORD:  'emailpassword',
        GOOGLE:         'google',
        ANONYM:         'anonym'
    };}

    /**
     * Gets the user information from the specified Firebase user.
     * 
     * @param   {Object}    user        Firebase user
     * @return  {Object}    userinfo    user data
     */

    static _getUserInfo(user) {

        // Getting user data from the Firebase user

        let usr = {
            uid:    user.uid
        };

        // Trying to get the name of the user
        
        if (user.displayName) {
            usr.name = user.displayName;
        }

        // Trying to get the email of the user

        if (user.providerData && user.providerData.length > 0) {
            usr.email = user.providerData[0].email;
        }

        // Returns the user data

        return usr;
    }

    /**
     * Firebase startup process for login.
     * 
     * @param   {String}    apiKey      API key of the Firebase project
     * @param   {String}    projectID   project ID of the Firebase project
     * @return  {Promise}   promise     promise object
     */

    static startup(apiKey, projectID) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Firebase version

            const VERSION = '4.10.1';

            // Loading the Firebase scripts

            $.loadScript([
                `https://www.gstatic.com/firebasejs/${VERSION}/firebase-app.js`,
                `https://www.gstatic.com/firebasejs/${VERSION}/firebase-auth.js`,
                `https://www.gstatic.com/firebasejs/${VERSION}/firebase-database.js`,
                `https://www.gstatic.com/firebasejs/${VERSION}/firebase-storage.js`
            ]).then(() => {

                // Setting the Firebase application

                firebase.initializeApp({
                    apiKey:         apiKey,
                    authDomain:     `${projectID}.firebaseapp.com`,
                    databaseURL:    `https://${projectID}.firebaseio.com`,
                    storageBucket:  `${projectID}.firebaseapp.com`,
                    projectId:      projectID
                });

                // Getting unsubscribe function definition and authenticating

                let unsubscribe = firebase.auth().onAuthStateChanged((user) => {
                    unsubscribe();

                    // Getting the user data

                    if (user) {
                        resolve(Firebase._getUserInfo(user));
                    } else {

                        // Redirection

                        firebase.auth().getRedirectResult().then((result) => {
                            if (result && result.user) {
                                resolve(Firebase._getUserInfo(user));
                            } else {
                                resolve();
                            }
                        }).catch((error) => {

                            // Login failed

                            reject(error);
                        });
                    }
                });
            }).catch((error) => {

                // Script loading failed

                reject(error);                
            });
        });
    }

    /**
     * Log-ins the current user by using the specified authentication
     * provider.
     * 
     * @param   {String}    provider        authentication provider
     * @param   {String}    email           email address of the user (Optional)
     * @param   {String}    password        password of the user (Optional)
     * @return  {Object}    promise         promise object with the user
     */

    static login(provider, email, password) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Checking the authentication provider

            if (!provider || 
            ![Firebase.PROVIDER.EMAILPASSWORD, Firebase.PROVIDER.GOOGLE,
            Firebase.PROVIDER.ANONYM].includes(provider)) {
                reject('Missing or unknown authentication provider');
            } else {       
                let unsubscribe = firebase.auth().onAuthStateChanged((user) => {
                    if (user) {
                        unsubscribe();
                        resolve(Firebase._getUserInfo(user));
                    } else {

                        // Logging in with Google acount

                        if (provider === Firebase.PROVIDER.GOOGLE) {
                            firebase.auth().getRedirectResult().then((result) => {
                                if (result && result.user) {
                                    resolve(Firebase._getUserInfo(result.user));
                                } else {
                                    let provider = new firebase.auth.GoogleAuthProvider();
                                    provider.addScope('profile');
                                    provider.addScope('email');
                                    firebase.auth().signInWithRedirect(provider);
                                }
                            }).catch((error) => {

                                // Login failed

                                reject(error);
                            });

                        // Logging in with anonymously

                        } else if (provider === Firebase.PROVIDER.ANONYM) {
                            firebase.auth().signInAnonymously().catch((error) => {

                                // Login failed

                                reject(error);
                            });

                        // Logging in with email and password
                            
                        } else if (provider === Firebase.PROVIDER.EMAILPASSWORD) {
                            firebase.auth().signInWithEmailAndPassword(email, password).catch((error) => {

                                // Login failed

                                reject(error);
                            });
                        // Unknown provider

                        } else {
                            reject('Unknown authentication provider: ' + provider);
                        }
                    }
                });
            }
        });
    }

    /**
     * Creates a new user.
     * 
     * @param   {String}    userEmail       email address of the user
     * @param   {String}    adminEmail      email address of the admin
     * @param   {String}    adminPassword   password of the admin
     * @param   {String}    locale          locale of the password reset email. Default: en
     * @return  {Object}    promise         promise object
     */

    static createUser(userEmail, adminEmail, adminPassword, locale) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Generating random password

            let userPassword = Math.random().toString(36).substring(7) + new Date().getTime();

            // Creating user

            firebase.auth().createUserWithEmailAndPassword(userEmail, userPassword).then(() => {

                // Getting the uid of the new user

                let uid = firebase.auth().currentUser.uid;

                // Logging out

                firebase.auth().signOut().then(() => {

                    // Logging in the admin

                    firebase.auth().signInWithEmailAndPassword(adminEmail, adminPassword).then(() => {

                        // Sending password reset email

                        Firebase.forgotPassword(userEmail, locale).then(() => {

                            // User created successfully

                            resolve(uid);
                        }).catch((error) => {

                            // Error occured during the sending email

                            reject(error);
                        });
                    }).catch((error) => {

                        // Error occured during the logging in

                        reject(error);
                    });
                }).catch((error) => {

                    // Error occured during the signing out

                    reject(error);
                });
            }).catch((error) => {

                // Error occured during the creation of the user

                reject(error);
            });
        });
    }

    /**
     * Log-outs the current user.
     *
     * @return  {Object}    promise         promise object
     */

    static logout() {
        
        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Checking login status

            if (firebase.auth().currentUser) {

                // Logging out

                firebase.auth().signOut().then(() => {
                    resolve();
                }).catch((error) => {
                    reject(error);
                });
            } else {
                reject('Not logged in');
            }
        });
    }

    /**
     * Sends a "password-reset" email to the specified email address.
     *
     * @param   {String}    email           email address of the user
     * @param   {String}    locale          locale of the email. Default: en
     * @return  {Object}    promise         promise object
     */

    static forgotPassword(email, locale) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Setting the locale of the email

            let auth = firebase.auth();
            auth.languageCode = locale || 'en';

            // Sending "password-reset" email

            auth.sendPasswordResetEmail(email).then(() => {

                // Email sent successfully

                resolve();
            }).catch((error) => {
                reject(error);
            });
        });
    }

    /**
     * Deletes the current user and logs out.
     *
     * @param   {Function}  userRemover     user remover function with
     *                                      promise (Optional)
     * @param   {Object}    currentUser     current user (Optional)
     * @return  {Object}    promise         promise object
     */

    static deleteUser(userRemover, currentUser) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Getting the current Firebase user

            let user = firebase.auth().currentUser;

            if (user) {

                // User eraser

                let eraseUser = () => {

                    // Deleting the user

                    user.delete().then(() => {

                        // Logging out the user

                        firebase.auth().signOut().then(() => {
                            resolve();
                        }).catch((error) => {
                            reject(error);
                        });
                    }).catch((error) => {
                        reject(error);
                    });                      
                };

                // Removing the current user from the Firebase if it is not an
                // anomymous user.

                if (user.isAnonymous === false) {
                    if (userRemover) {
                        userRemover(currentUser).then(() => {
                            eraseUser();
                        }).catch((error) => {
                            reject(error);
                        });
                    }
                } else {
                    eraseUser();
                }
            } else {
                reject('Not logged in');            
            }
        });
    }

    /**
     * Gets the absolute download URL of the specified file.
     *
     * @param   {String}    file            relative file path
     * @return  {Object}    promise         promise object
     */    

    static getFileURL(file) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Getting the absolute download URL

            firebase.storage().refFromURL(
                `gs://${firebase.app().options.storageBucket.split('.')[0]}.appspot.com/${file}`
            ).getDownloadURL().then((url) => {
                resolve(url);
            }).catch((error) => {
                reject(error);
            });
        });
    }

    /**
     * Uploads the specified file.
     *
     * @param   {String}                filename        filename
     * @param   {ArrayBuffer/String}    content         content of the file. Can be BASE64 content as well.
     * @param   {String}                mimetype        mime type of the file
     * @return  {Object}                promise         promise object
     */ 

    static uploadFile(filename, content, mimetype) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Getting storage bucket

            let bucket = firebase.app().options.storageBucket.split('.')[0];

            // Uploading the file if it is a BASE64 content

            if ($.isString(content)) {

                // Cutting off the header of the content

                if (content.indexOf('data:') === 0) {
                    content = content.substring(content.indexOf(',') + 1);
                }

                // Uploading

                firebase.storage().refFromURL(
                    `gs://${bucket}.appspot.com/${filename}`
                ).putString(content, 'base64', { contentType: mimetype }).then((snapshot) => {

                    // Getting the download URL
                    
                    snapshot.ref.getDownloadURL().then((downloadURL) => {
                        resolve(downloadURL);
                    }).catch((error) => {
                        reject(error);
                    });
                }).catch((error) => {
                    console.log(error);
                    reject(error);
                });
            } else {

                // Uploading the array buffer version of the file

                firebase.storage().refFromURL(
                    `gs://${bucket}.appspot.com/${filename}`
                ).put(content, { contentType: mimetype }).then((snapshot) => {
                    resolve(snapshot);
                }).catch((error) => {
                    reject(error);
                });
            }
        });        
    }

    /**
     * Deletes the specified file.
     *
     * @param   {String}    file            relative file path
     * @return  {Object}    promise         promise object
     */    

    static deleteFile(file) {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Getting the absolute download URL

            firebase.storage().refFromURL(
                `${file}`
            ).delete().then(() => {
                resolve();
            }).catch((error) => {
                reject(error);
            });
        });
    }
}



// WEBPACK FOOTER //
// ./src/components/Util/Firebase.js